import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination/Pagination';
import SearchInput from '../../components/SearchInput/SearchInput';
import { LANG_TEXT } from '../../constants/language';
import { useOrderContext } from '../../context/OrderContext';
import { useOrderStadiumContext } from '../../context/OrderStadiumContext';
import usePermission from '../../hooks/usePermission';
import { getEndOfDay, getStartOfDay } from '../../utils/handleDateTime';
import DatePickerCustom from '../OrderHistory/components/DatePicker/DatePicker';
import FoodTruckFilter from '../OrderHistory/components/FoodTruckFilter';
import Loader from './components/Loader/Loader';
import TableBodyCustom from './components/TableBody/TableBody';
import TableHeadCustom from './components/TableHead/TableHead';
import { useStyles } from './styles';

export default function OrderManagment() {
  const classes = useStyles();
  const { showForSuperAdmin, showForAdmin } = usePermission();
  const { allOrders, loading } = useOrderContext();
  const { allOrders: stadiumOrders, loading: stadiumLoading } = useOrderStadiumContext();

  const [page, setPage] = useState<number>(0);
  const [selectedFoodTrucks, setSelectedFoodTrucks] = useState<string[]>([]);
  const [orderIdSearch, setOrderIdSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const [sortBy, setSortBy] = useState<'createTime' | 'deliveryTime'>('createTime');
  const { t } = useTranslation();
  const rowsPerPage = 10;

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (selected: string[]) => {
    setSelectedFoodTrucks(selected);
    setPage(0);
  };

  const handleDateSelect = (start: Date | null, end: Date | null) => {
    if (start && end) {
      setDateRange({ start, end });
    } else {
      setDateRange(null);
    }
    setPage(0);
  };

  const parsedAllOrders = useMemo(() => {
    if (showForSuperAdmin || showForAdmin) {
      return { orders: stadiumOrders, loading: stadiumLoading };
    }

    return { orders: allOrders, loading };
  }, [allOrders, stadiumOrders, showForSuperAdmin, showForAdmin, loading, stadiumLoading]);

  const filteredOrders = useMemo(() => {
    let filtered = parsedAllOrders.orders;

    // Filter by date if date range is selected
    if (dateRange) {
      filtered = filtered.filter((order) => {
        const orderTime = new Date(order.order_create_time.seconds * 1000);
        return orderTime >= getStartOfDay(dateRange.start) && orderTime <= getEndOfDay(dateRange.end);
      });
    }

    // Filter by food trucks if selected
    if (selectedFoodTrucks.length > 0) {
      filtered = filtered.filter((order) => {
        const singleOrder = order.restaurant_id && selectedFoodTrucks.includes(order.restaurant_id);
        const multipleOrder = order.multi_orders && order.multi_orders.some((item) => selectedFoodTrucks.includes(item.restaurant_id));

        return singleOrder || multipleOrder;
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'deliveryTime') {
        const timeA = a.requested_delivery_time ? a.requested_delivery_time.seconds : 0;
        const timeB = b.requested_delivery_time ? b.requested_delivery_time.seconds : 0;
        return timeA - timeB;
      } else {
        const timeA = a.order_create_time ? a.order_create_time.seconds : 0;
        const timeB = b.order_create_time ? b.order_create_time.seconds : 0;
        return timeB - timeA;
      }
    });

    if (orderIdSearch && orderIdSearch.trim() !== '') {
      const s = orderIdSearch.trim().toLowerCase();
      return sorted.filter((order) => (order.id || '').toLowerCase().includes(s));
    }

    return sorted;
  }, [parsedAllOrders.orders, selectedFoodTrucks, dateRange, sortBy, orderIdSearch]);

  return (
    <Box
      component="main"
      className={classes.Dashboard}
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography fontSize={24} lineHeight={'32px'} fontWeight={500} color={'var(--Gray-900, #101828)'}>
          {t(LANG_TEXT.ORDER_MANAGEMENT)}
        </Typography>
        <SearchInput
          value={orderIdSearch}
          onSearch={(v) => {
            setOrderIdSearch(v);
            setPage(0);
          }}
          placeholder={t(LANG_TEXT.SEARCH_BY_ORDER_ID)}
          debounceMs={1500}
        />
        {!parsedAllOrders.loading && (showForSuperAdmin || showForAdmin) && <DatePickerCustom onSelect={handleDateSelect} defaultToday={true} />}
      </Box>
      {parsedAllOrders.loading ? (
        <Loader />
      ) : (
        <Paper className={classes.Table}>
          <TableContainer className={classes.Scrollbar}>
            <Table stickyHeader aria-label="sticky table" sx={{ borderCollapse: 'collapse' }}>
              <TableHeadCustom style={classes.Table} showForSuperAdmin={showForSuperAdmin || showForAdmin} />
              <TableBodyCustom listOrder={filteredOrders} rowsPerPage={rowsPerPage} page={page} showForSuperAdmin={showForSuperAdmin || showForAdmin} />
            </Table>
          </TableContainer>
          {showForSuperAdmin || showForAdmin ? (
            <Box className={classes.Footer}>
              <FoodTruckFilter selectedFoodTrucks={selectedFoodTrucks} onFilterChange={handleFilterChange} />
              <RadioGroup row value={sortBy} onChange={(e) => setSortBy(e.target.value as 'createTime' | 'deliveryTime')} className={classes.RadioGroup}>
                <FormControlLabel value="createTime" control={<Radio size="small" />} label={t(LANG_TEXT.SORT_BY_ORDER_TIME)} />
                <FormControlLabel value="deliveryTime" control={<Radio size="small" />} label={t(LANG_TEXT.SORT_BY_DELIVERY_TIME)} />
              </RadioGroup>
              <Pagination data={filteredOrders} rowsPerPage={rowsPerPage} onPageChange={onPageChange} currentPage={page} />
            </Box>
          ) : (
            <Pagination data={filteredOrders} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
          )}
        </Paper>
      )}
    </Box>
  );
}
